import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockUtilizadores, mockPostos } from '../mockPostos';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Mail, Phone, Briefcase, Calendar, Battery, Zap } from 'lucide-react';
import { toast } from '../components/ui/sonner';

const UtilizadorDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = mockUtilizadores.find(u => u.id === id);
  const [permissoes, setPermissoes] = useState(user?.permissoes || {});
  const [isEditing, setIsEditing] = useState(false);

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Utilizador não encontrado</p>
        <Button onClick={() => navigate('/carregamento/utilizadores')} className="mt-4">
          Voltar à lista
        </Button>
      </div>
    );
  }

  const locais = ['Pragal', 'Santa Apolónia', 'Évora', 'Freixo'];

  const togglePermissao = (local, postoId) => {
    setPermissoes(prev => {
      const newPermissoes = { ...prev };
      if (!newPermissoes[local]) {
        newPermissoes[local] = [];
      }
      
      if (newPermissoes[local].includes(postoId)) {
        newPermissoes[local] = newPermissoes[local].filter(p => p !== postoId);
        if (newPermissoes[local].length === 0) {
          delete newPermissoes[local];
        }
      } else {
        newPermissoes[local] = [...newPermissoes[local], postoId];
      }
      
      return newPermissoes;
    });
  };

  const handleSavePermissoes = () => {
    // Aqui seria feita a chamada à API para salvar as permissões
    toast.success('Permissões atualizadas com sucesso!');
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/carregamento/utilizadores')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">{user.nome}</h1>
          <p className="text-gray-600 mt-1">Gestão de permissões e estatísticas</p>
        </div>
        <Badge className={user.ativo ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-800 border-gray-200'}>
          {user.ativo ? 'Ativo' : 'Inativo'}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Perfil */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center">
              <Avatar className="h-24 w-24 mb-4">
                <AvatarImage src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.nome)}&background=003D7A&color=fff`} />
                <AvatarFallback className="bg-[#003D7A] text-white text-2xl">
                  {user.nome.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <h2 className="text-xl font-bold text-gray-900 text-center">{user.nome}</h2>
              <p className="text-sm text-gray-600">{user.cargo}</p>
              <p className="text-xs text-gray-500 mt-1">{user.departamento}</p>
            </div>

            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-gray-500" />
                <span className="text-gray-700">{user.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-gray-500" />
                <span className="text-gray-700">{user.telefone}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span className="text-gray-700">Registo: {new Date(user.dataRegisto).toLocaleDateString('pt-PT')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Estatísticas */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Estatísticas de Utilização</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
                <div className="flex items-center justify-between mb-2">
                  <Battery className="h-8 w-8 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-blue-600">{user.totalCarregamentos}</p>
                <p className="text-sm text-gray-600 mt-1">Total de Carregamentos</p>
              </div>
              <div className="bg-green-50 rounded-lg p-6 border border-green-100">
                <div className="flex items-center justify-between mb-2">
                  <Zap className="h-8 w-8 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-green-600">{user.energiaConsumida}</p>
                <p className="text-sm text-gray-600 mt-1">Energia Consumida (kWh)</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-6 border border-orange-100">
                <div className="flex items-center justify-between mb-2">
                  <Calendar className="h-8 w-8 text-orange-600" />
                </div>
                <p className="text-3xl font-bold text-orange-600">{user.tempoTotal}h</p>
                <p className="text-sm text-gray-600 mt-1">Tempo Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Permissões */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Permissões de Carregamento</CardTitle>
            {!isEditing ? (
              <Button onClick={() => setIsEditing(true)}>Editar Permissões</Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => {
                  setPermissoes(user.permissoes);
                  setIsEditing(false);
                }}>Cancelar</Button>
                <Button onClick={handleSavePermissoes}>Guardar</Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {locais.map(local => {
              const postosDoLocal = mockPostos.filter(p => p.local === local);
              
              return (
                <div key={local} className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">{local}</h3>
                  <div className="space-y-2">
                    {postosDoLocal.map(posto => {
                      const hasPermission = permissoes[local]?.includes(posto.id);
                      
                      return (
                        <div key={posto.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={hasPermission}
                              onCheckedChange={() => togglePermissao(local, posto.id)}
                              disabled={!isEditing}
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{posto.nome}</p>
                              <p className="text-xs text-gray-600">{posto.tipo} - {posto.potencia} kW</p>
                            </div>
                          </div>
                          <Badge className={posto.estado === 'Disponível' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-800 border-gray-200'}>
                            {posto.estado}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UtilizadorDetalhe;
